import {CommonModule} from '@angular/common';
import {
  Component,
  inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {GateweeAssetUploadComponent} from '../gatewee-asset-upload/gatewee-asset-upload.component';
import {GateweeSelectV3Component} from '../gatewee-select-v3/gatewee-select-v3.component';

@Component({
  selector: 'gatewee-style-settings',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    GateweeAssetUploadComponent,
    GateweeSelectV3Component,
  ],
  templateUrl: './gatewee-style-settings.component.html',
  styleUrls: ['./gatewee-style-settings.component.css'],
})
export class GateweeStyleSettingsComponent implements OnInit, OnChanges {
  private readonly translate = inject(TranslateService);
  @Input() formGroup?: FormGroup;
  @Input() domainId?: number;

  private fb = new FormBuilder();
  private _formGroup!: FormGroup;

  get internalFormGroup(): FormGroup {
    return this.formGroup || this._formGroup;
  }

  get logoControl(): FormControl<string | null> {
    return this.internalFormGroup.get('logo') as FormControl<string | null>;
  }

  get logoBlackControl(): FormControl<string | null> {
    return this.internalFormGroup.get('logoBlack') as FormControl<
      string | null
    >;
  }

  get logoWhiteControl(): FormControl<string | null> {
    return this.internalFormGroup.get('logoWhite') as FormControl<
      string | null
    >;
  }

  get logoSymbolControl(): FormControl<string | null> {
    return this.internalFormGroup.get('logoSymbol') as FormControl<
      string | null
    >;
  }

  get faviconControl(): FormControl<string | null> {
    return this.internalFormGroup.get('favicon') as FormControl<string | null>;
  }

  themeOptions = [
    {
      label: this.translate.instant(
        'features.domains.settings.style.theme.light',
      ),
      value: 'light',
    },
    {
      label: this.translate.instant(
        'features.domains.settings.style.theme.dark',
      ),
      value: 'dark',
    },
  ];

  ngOnInit(): void {
    // Initialize formGroup - either use provided one or create default
    this.initializeFormGroup();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Handle formGroup being set after initialization (when loaded dynamically)
    if (changes['formGroup']) {
      if (this.formGroup && !this._formGroup) {
        // FormGroup was provided, no need to create default
      } else if (this.formGroup && this._formGroup) {
        // FormGroup was provided after we created a default
        // The getter will use the provided one
      } else if (!this.formGroup && !this._formGroup) {
        // FormGroup was removed, create default if needed
        this.initializeFormGroup();
      }
    }
  }

  private initializeFormGroup(): void {
    // Only create default formGroup if none is provided and we haven't created one yet
    if (!this.formGroup && !this._formGroup) {
      this._formGroup = this.fb.group({
        primaryColor: ['#0066CC'],
        secondaryColor: ['#003366'],
        accentColor: ['#0099FF'],
        logo: [''],
        logoBlack: [''],
        logoWhite: [''],
        logoSymbol: [''],
        favicon: [''],
        theme: ['light'],
      });
    }
  }

  onColorChange(controlName: string, event: Event): void {
    const input = event.target as HTMLInputElement;
    const control = this.internalFormGroup.get(controlName);
    if (control) {
      control.setValue(input.value);
      control.markAsTouched();
    }
  }
}
